import {inject, Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, catchError, Observable, of, switchMap, timeout} from "rxjs";
import {ExternalApiService} from "../../../../core/api/external-api/external-api.service";
import * as Sentry from "@sentry/angular";
import {ConfigService} from "../../../../core/config";
import {CbdResultDto} from "../../../../core/dto/cbd-result-dto";
import {CreateCbdRequestDto} from "../../../../core/dto/create-cbd-dto";
import {Picture, PictureStatus} from "../../../../core/models/picture";
import {DomSanitizer} from "@angular/platform-browser";
import {TyrePicture} from "../../../../core/models/tyrePicture";
import {isEqual} from "lodash";
import {Step, StepType} from "../../../../core/models/step";
import {isTyre, StoredPicture, UploadState} from "../../../../core/models/stored-picture";

@Injectable({
    providedIn: 'root'
})
export class PhotoshootService {

  private readonly _sessionStorage = sessionStorage;

  protected externalApi = inject(ExternalApiService);
  protected config = inject(ConfigService);
  protected ngZone = inject(NgZone);
  private sanitizer = inject(DomSanitizer);

  private readonly _pictures: BehaviorSubject<TyrePicture[]> = new BehaviorSubject<TyrePicture[]>([]);
  readonly pictures$ = this._pictures.asObservable();

  private static readonly CBD_TIMEOUT = 5000;

  sendCBD(analysisId: number, step: Step, blob: Blob, dimensions: {width: number, height: number}) {
      const request = new CreateCbdRequestDto(Step.getTyrePositionDto(step.position), dimensions.width, dimensions.height);
      return this.externalApi.createCbd(analysisId, step.type, request, new File([blob], 'cbd.jpg')).pipe(
          switchMap((response) => this.setMercureEventSource(response.mercureTopic, response.sseTyreAnalysisToken)),
          catchError((error) => {
              Sentry.captureMessage(`Unable to send ${step.type.toLowerCase()} picture for CBD`, {extra: {error}});
              return of({isCBDValid: undefined} as CbdResultDto);
          }),
          timeout(PhotoshootService.CBD_TIMEOUT)
        )
  }

  private setMercureEventSource(topic: string, token: string): Observable<CbdResultDto> {
      return new Observable((observer) => {
          const url = new URL(this.config.mercureUrl);
          url.searchParams.append('topic', topic);
          url.searchParams.append(
              'authorization',
              token
          );
          const eSource = new EventSource(url.toString(), {
              withCredentials: false
          });
          eSource.onerror = (event) => {
              Sentry.captureException(new Error('CBD Mercure event error'), {extra: {event}});
              observer.error(new Error('CBD Mercure event error'));
          };
          eSource.onmessage = (event) => {
              this.ngZone.run(() => {
                  const result: CbdResultDto = JSON.parse(event.data)
                  observer.next(result);
                  observer.complete();
              })
              eSource.close();
          }

          return () => {
              eSource.close();
          }
      })
  }

  async associatePictureBlob(picture: Picture, blob: Blob) {
    const buffer = await blob.arrayBuffer();
    picture.data = new Uint8Array(buffer.slice(0));
    picture.url = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
    return picture;
  }

  applyStoredPictures(stored: StoredPicture[], confirmedSteps: string[] = []) {
    const byKey = new Map<string, TyrePicture>();
    stored
      .filter(isTyre)
      .forEach(stored => {
        const picture = new TyrePicture(new Step(stored.step.position, stored.step.type));
        picture.status = PhotoshootService.toPictureStatus(stored.uploadState);
        byKey.set(picture.key, picture);
      });
    confirmedSteps
      .filter(key => !byKey.has(key))
      .forEach(key => {
        const picture = new TyrePicture(Step.fromString(key));
        picture.status = PictureStatus.VALID;
        byKey.set(key, picture);
      });
    this.setPictures([...byKey.values()]);
  }

  private static toPictureStatus(state: UploadState): PictureStatus {
    switch (state) {
      case UploadState.UPLOADED: return PictureStatus.VALID;
      case UploadState.FAILED: return PictureStatus.ERROR;
      default: return PictureStatus.UPLOADING;
    }
  }

  getPictures(): TyrePicture[] {
    return this._pictures ? this._pictures.getValue() : [];
  }

  setPicture(picture: TyrePicture) {
    const pictures = this.getUpdatedPictures(picture, this.getPictures());
    this._pictures.next(pictures);
  }

  setPictures(p: TyrePicture[]) {
    this._pictures.next([...p]);
  }

  clearPictures() {
    this._pictures.next([]);
  }

  // Emit new array to notify signals
  private getUpdatedPictures(picture: TyrePicture, savedPictures: TyrePicture[]) {
    const existingIndex = savedPictures.findIndex(p => isEqual(p.step, picture.step));
    if (existingIndex === -1) {
      return [...savedPictures, picture];
    }
    const existingPicture = savedPictures[existingIndex];
    existingPicture.url = picture.url;
    existingPicture.data = picture.data;
    existingPicture.status = picture.status;
    return [...savedPictures];
  }

  // TODO: Do not use session storage directly here.
  setCamera(deviceId: string, step: StepType) {
    this._sessionStorage.setItem(`camera.${step}`, deviceId)
  }

  getCamera(step: StepType) {
    return this._sessionStorage.getItem(`camera.${step}`);
  }
}
